import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Camera, Image, Sparkles, ArrowRight, UserCircle } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { formatPrice } from '../../lib/utils';
import { cn } from '../../lib/utils';
import { leadAPI } from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIChat() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      content: "Bonjour ! Je suis votre assistant style VyaStore. 👋\n\nJe peux vous aider à trouver le look parfait, analyser une photo de votre style, ou répondre à vos questions. Que puis-je faire pour vous ?",
      looks: []
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() && !selectedImage) return;

    const userMessage = {
      type: 'user',
      content: input,
      image: selectedImage
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSelectedImage(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          image_url: selectedImage
        })
      });
      
      const data = await response.json();
      
      setMessages(prev => [...prev, {
        type: 'bot',
        content: data.response,
        looks: data.suggested_looks || [],
        generatedImage: data.generated_image_url
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        type: 'bot',
        content: "Désolé, une erreur s'est produite. Veuillez réessayer.",
        looks: []
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleContactAdvisor = async () => {
    try {
      if (!user) {
        toast.error('Connectez-vous pour contacter un conseiller');
        navigate('/login');
        return;
      }
      // Fallback: crée un lead marqué pour contact conseiller
      await leadAPI.create({
        email: user.email,
        source: 'POPUP',
        consent_marketing: false,
        tags: ['advisor_request']
      });
      toast.success('Conseiller contacté ! Redirection...');
      setTimeout(() => navigate('/account'), 1500);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la connexion au conseiller');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const quickActions = [
    { label: "Looks femme", message: "Montre-moi des looks pour femme" },
    { label: "Looks homme", message: "Montre-moi des looks pour homme" },
    { label: "Style casual", message: "Je cherche un look casual" },
    { label: "Tenue de soirée", message: "Je cherche une tenue pour une soirée" }
  ];

  return (
    <>
      {/* Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center",
          "bg-foreground text-background hover:bg-foreground/90 transition-colors",
          isOpen && "hidden"
        )}
        data-testid="chat-button"
      >
        <MessageCircle className="h-6 w-6" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background" />
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            className="fixed bottom-6 right-6 z-50 w-[380px] h-[600px] bg-card rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-foreground text-background">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-background/20 flex items-center justify-center">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Assistant Style</h3>
                  <p className="text-xs opacity-70">Propulsé par IA</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsOpen(false)}
                className="text-background hover:bg-background/20"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn(
                      "flex",
                      msg.type === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    <div className={cn(
                      "max-w-[85%] rounded-2xl p-3",
                      msg.type === 'user' 
                        ? "bg-foreground text-background rounded-br-sm" 
                        : "bg-muted rounded-bl-sm"
                    )}>
                      {msg.image && (
                        <img 
                          src={msg.image} 
                          alt="Uploaded" 
                          className="w-full h-32 object-cover rounded-lg mb-2"
                        />
                      )}
                      <p className="text-sm whitespace-pre-line">{msg.content}</p>
                      
                      {/* Suggested Looks */}
                      {msg.looks && msg.looks.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {msg.looks.map((look) => (
                            <Link
                              key={look.id}
                              to={`/looks/${look.slug}`}
                              onClick={() => setIsOpen(false)}
                              className="flex gap-3 p-2 bg-background/10 rounded-lg hover:bg-background/20 transition-colors"
                            >
                              <div className="w-16 h-20 rounded overflow-hidden flex-shrink-0">
                                <img 
                                  src={look.images?.[0]} 
                                  alt={look.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{look.name}</p>
                                <p className="text-xs opacity-70">{look.style} • {look.occasion}</p>
                                <p className="text-sm font-semibold mt-1">
                                  {formatPrice(look.bundle_price_cents)}
                                </p>
                              </div>
                              <ArrowRight className="h-4 w-4 self-center opacity-50" />
                            </Link>
                          ))}
                        </div>
                      )}
                      
                      {/* Generated Image */}
                      {msg.generatedImage && (
                        <div className="mt-3">
                          <p className="text-xs opacity-70 mb-2">Image générée :</p>
                          <img 
                            src={msg.generatedImage} 
                            alt="Generated look"
                            className="w-full rounded-lg"
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {loading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-muted rounded-2xl rounded-bl-sm p-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-foreground/30 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </ScrollArea>

            {/* Quick Actions */}
            {messages.length === 1 && (
              <div className="px-4 pb-2">
                <div className="flex gap-2 flex-wrap">
                  {quickActions.map((action) => (
                    <button
                      key={action.label}
                      onClick={() => {
                        setInput(action.message);
                      }}
                      className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-colors"
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Image Preview */}
            {selectedImage && (
              <div className="px-4 pb-2">
                <div className="relative inline-block">
                  <img 
                    src={selectedImage} 
                    alt="Selected" 
                    className="h-16 rounded-lg"
                  />
                  <button
                    onClick={() => setSelectedImage(null)}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t border-border">
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-shrink-0"
                  title="Ajouter une photo"
                >
                  <Camera className="h-4 w-4" />
                </Button>
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder="Décrivez votre style..."
                  className="flex-1"
                  disabled={loading}
                />
                <Button 
                  onClick={handleSend}
                  disabled={loading || (!input.trim() && !selectedImage)}
                  className="flex-shrink-0"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Propulsé par Gemini • Mock data activé
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleContactAdvisor}
                className="w-full mt-2"
              >
                <UserCircle className="h-4 w-4 mr-2" />
                Parler à un conseiller humain
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
